import { FundCategory } from '@/api/fund/queries';
import { Country } from '@/api/lookup/types';
import { StepForms } from '@/stores/slices/setup-fund-slice';
import { isValidHttpUrl } from '@/utils/http-url';

import { FundStructure } from '../constants/fund-structure';
import { isFeederComplete } from './feeder-helpers';
import { isFeederUsApplicable, isFundUsApplicable } from './is-us-applicable';
import { validateShareClasses, validateSubscriptionOpts } from './validate-fund-fields';
import { isJurisdictionRegionRequired } from './validate-step';

export type ValidationContext = {
  fundCategories?: FundCategory[];
  countries?: Country[];
};

export type SubStepValidator = (_forms: StepForms, _ctx: ValidationContext) => boolean;

const alwaysValid: SubStepValidator = () => true;

const isMasterFeeder = (forms: StepForms) => forms.fundStructure === FundStructure.MasterFeeder;

type DocumentBundleKey = 'offeringDocuments' | 'subscriptionDocuments' | 'riskDisclosure';

const validateDocumentBundle =
  (key: DocumentBundleKey): SubStepValidator =>
  (forms) => {
    if (forms.fundStructure === FundStructure.ManagedAccountSma) return true;
    if (isMasterFeeder(forms)) {
      return forms.feeders.every((f) =>
        Boolean(f[key].notes?.trim() && (f[key].documentIds?.length ?? 0) > 0),
      );
    }
    return Boolean(forms[key]?.notes?.trim() && (forms[key]?.documentIds?.length ?? 0) > 0);
  };

export const SUB_STEP_VALIDATORS: Record<string, SubStepValidator> = {
  '01': (forms, ctx) => {
    if (!forms.fundStructure) return false;
    const isMF = isMasterFeeder(forms);
    const jurisdictionCountry = forms.investmentDescription?.jurisdictionCountry?.trim();
    const shouldRequireJurisdictionState = isJurisdictionRegionRequired(
      jurisdictionCountry,
      ctx.countries,
    );
    const website = String(forms.investmentDescription?.website ?? '');
    const category = ctx.fundCategories?.find(
      (c) => c.code === forms.investmentDescription?.category,
    );

    const masterLegalJurisdictionOk = isMF
      ? true
      : Boolean(
          forms.investmentDescription.legalStructure?.trim() &&
            jurisdictionCountry &&
            (shouldRequireJurisdictionState
              ? forms.investmentDescription?.jurisdictionState?.trim()
              : true),
        );

    const feedersOk = isMF
      ? forms.feeders.length >= 2 && forms.feeders.every(isFeederComplete)
      : true;

    const feederNamesUnique = isMF
      ? new Set(forms.feeders.map((f) => (f.name ?? '').trim().toLowerCase())).size ===
        forms.feeders.length
      : true;

    return Boolean(
      forms.investmentDescription?.name?.trim() &&
        isValidHttpUrl(website) &&
        forms.investmentDescription?.category?.trim() &&
        masterLegalJurisdictionOk &&
        feedersOk &&
        feederNamesUnique &&
        ((category?.assets?.length ?? 0 > 0)
          ? (forms.investmentDescription?.assets?.length ?? 0 > 0)
          : true) &&
        ((category?.stages?.length ?? 0 > 0)
          ? (forms.investmentDescription?.stage?.length ?? 0 > 0)
          : true) &&
        ((category?.strategies?.length ?? 0 > 0)
          ? (forms.investmentDescription?.strategies?.length ?? 0 > 0)
          : true) &&
        (forms.investmentDescription?.description?.trim()?.length ?? 0) >= 10,
    );
  },

  '02': alwaysValid,

  '03': (forms) =>
    Boolean(
      (forms.duration.isNewFund || forms.duration.startDate?.trim()) &&
        (forms.duration.isOpenEnded || forms.duration.durationInYears),
    ),

  '04': (forms) => forms.accountingType !== null && forms.returnProfile.feeType !== null,

  '05': (forms) =>
    Boolean(
      forms.redemption.noRedemptionAllowed ||
        ((forms.redemption.lockUpPeriod?.length ?? 0) > 0 &&
          forms.redemption.frequency?.trim() &&
          forms.redemption.priority?.trim() &&
          ((forms.redemption.max !== null && forms.redemption.max > 0) ||
          (forms.redemption.gates && forms.redemption.gates?.toString().trim()) ||
          forms.redemption.months !== null
            ? forms.redemption.max !== null &&
              forms.redemption.max > 0 &&
              !!(forms.redemption.gates && forms.redemption.gates?.toString().trim()) &&
              forms.redemption.months !== null
            : true)),
    ),

  '06': (forms) =>
    Boolean(
      forms.feesData.fees?.every((line) =>
        Boolean(
          line.feeType?.trim() &&
            line.percentage !== undefined &&
            line.percentage !== null &&
            line.type?.trim() &&
            line.frequency?.trim(),
        ),
      ),
    ),

  '07': (forms) =>
    Boolean(
      forms.minInvestmentAmounts.currency?.trim() &&
        forms.minInvestmentAmounts.lines?.every((line) =>
          line.notApplicable
            ? true
            : line.value !== null && Number(line.value) > 0 && line.description?.trim(),
        ),
    ),

  '08': (forms) =>
    Boolean(
      forms.distributionPeriod.frequencyNotApplicable ||
        (forms.distributionPeriod.period?.trim() &&
          forms.distributionPeriod.type?.trim() &&
          forms.distributionPeriod.frequency),
    ),

  '09': (forms) =>
    Boolean(forms.reportingPeriod.reportingNotApplicable || forms.reportingPeriod.period?.trim()),

  '10': (forms) => {
    if (!isMasterFeeder(forms)) return true;
    if ((forms.unassignedCountries?.length ?? 0) > 0) return false;
    return forms.feeders.every(
      (f) =>
        (f.investorEligibility.allowed?.length ?? 0) +
          (f.investorEligibility.partiallyRestricted?.length ?? 0) >
        0,
    );
  },

  '11': (forms) => {
    if (!isMasterFeeder(forms)) {
      return !isFundUsApplicable(forms) || Boolean(forms.investmentCompaniesActStatus);
    }
    return forms.feeders.every(
      (f) => !isFeederUsApplicable(f) || Boolean(f.investmentCompaniesActStatus),
    );
  },

  '12': (forms, ctx) =>
    validateDocumentBundle('offeringDocuments')(forms, ctx) &&
    validateDocumentBundle('subscriptionDocuments')(forms, ctx) &&
    validateDocumentBundle('riskDisclosure')(forms, ctx),

  '13': alwaysValid,
  '14': alwaysValid,

  '15': (forms) =>
    Boolean(
      forms.howFundWorks?.whatItInvestsIn?.trim() &&
        forms.howFundWorks?.howItAimsToMakeMoney?.trim() &&
        forms.howFundWorks?.whatCanGoWrong?.trim(),
    ),

  '16': alwaysValid,

  '17': (forms) =>
    (forms.fundBenefits ?? []).every((b) => b.title?.trim() && b.description?.trim()),

  '18': (forms) => {
    const idealProfile = forms.fundTargetInvestor?.idealInvestorProfile ?? [];
    const whoNotFor = forms.fundTargetInvestor?.whoItsNotFor ?? [];
    return (
      idealProfile.length > 0 &&
      idealProfile.every((item) => item.trim()) &&
      whoNotFor.length > 0 &&
      whoNotFor.every((item) => item.trim())
    );
  },

  '19': alwaysValid,

  '20': (forms) => {
    const faqItems = forms.faq ?? [];
    return (
      faqItems.length > 0 &&
      faqItems.every((item) => Boolean(item.question?.trim() && item.answer?.trim()))
    );
  },

  '21': (forms) =>
    Boolean(
      forms.investmentAvailabilityStatus?.trim() &&
        forms.transferability !== null &&
        forms.transferability !== undefined,
    ),

  '22': (forms, ctx) => {
    const category = ctx.fundCategories?.find(
      (c) => c.code === forms.investmentDescription?.category,
    );
    const isSMA = category?.code === 'MAS';

    if (isSMA) return true;

    return isMasterFeeder(forms)
      ? forms.feeders.every((f) => validateShareClasses(f.numberUnits.shareClasses))
      : validateShareClasses(forms.numberUnits.shareClasses);
  },

  '23': (forms) =>
    isMasterFeeder(forms)
      ? forms.feeders.every((f) => validateSubscriptionOpts(f.subscriptionOptions))
      : validateSubscriptionOpts(forms.subscriptionOptions),
};
